import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  TerminalSquare,
  Shield,
  Server,
  Cpu,
  Lock,
  Network,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const COLORS = {
  bg: "bg-white-950",
  panel: "bg-neutral-900/90",
  panelLight: "bg-neutral-900/70",
  border: "border-neutral-800",
  text: "text-neutral-100",
  sub: "text-neutral-400",
  green: "text-emerald-400",
  red: "text-red-400",
  blue: "text-sky-400",
  amber: "text-amber-300",
};

const PROMPT = "student@redhat:~$";

const syllabus = {
  RHCSA: [
    "Essential GNU/Linux commands & help utilities",
    "Users, groups, sudoers & permissions (chmod, chown, umask)",
    "Storage: partitions, LVM, mounting, fstab, swap",
    "Systemd services, boot targets, journald logs",
    "Networking basics, nmcli, firewalld",
    "Filesystem hierarchy, archives, package mgmt (dnf)",
    "SELinux modes & contexts, troubleshooting",
  ],
  RHCE: [
    "Automation with Ansible: inventory, modules, roles",
    "Service configuration: HTTPD/Nginx, proxies, TLS",
    "Advanced networking: bonding/teams, VLANs, routing",
    "Identity & access: SSH hardening, PAM & sudo policies",
    "Storage services: NFS/SMB exports, autofs",
    "Security: firewalld zones, rich rules, fail2ban",
    "Troubleshooting, performance tuning, system profiling",
  ],
};

const labs = [
  {
    title: "CLI Mastery",
    desc: "Write shell pipelines, manage processes, schedule cron jobs.",
  },
  {
    title: "Server Build",
    desc: "Provision a web server with TLS, virtual hosts & logging.",
  },
  {
    title: "Ansible Automation",
    desc: "Idempotent playbooks for users, packages, services & handlers.",
  },
  {
    title: "Storage & SELinux",
    desc: "LVM, fstab & SELinux context fixes for services.",
  },
];

const careers = [
  "Linux System Administrator",
  "DevOps Engineer",
  "Cloud/Platform Engineer",
  "SRE (Site Reliability Engineer)",
  "Infrastructure Engineer",
  "Security Operations (SecOps)",
];

// Small util: typewriter effect
function useTypewriter(text, speed = 24, start = true) {
  const [out, setOut] = useState("");
  useEffect(() => {
    if (!start) return;
    setOut("");
    let i = 0;
    const id = setInterval(() => {
      setOut((prev) => prev + text.charAt(i));
      i++;
      if (i >= text.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [text, start]);
  return out;
}

export default function RedHat() {
  const [track, setTrack] = useState("RHCSA");
  const [command, setCommand] = useState(`man ${track.toLowerCase()}`);
  const [history, setHistory] = useState([
    `${PROMPT} whoami`,
    "student",
    `${PROMPT} hostnamectl --static`,
    "workstation",
  ]);
  const [typing, setTyping] = useState(true);
  const outputRef = useRef(null);

  const typed = useTypewriter(`${PROMPT} ${command}`, 12, typing);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history, command, typed]);

  const runCommand = (cmd) => {
    setTyping(false);
    setCommand(cmd);
    setTimeout(() => {
      const out = handleCommand(cmd, track);
      setHistory((h) => [...h, `${PROMPT} ${cmd}`, ...out]);
      setTyping(true);
    }, 150);
  };

  const handleCommand = (cmd, currentTrack) => {
    const clean = cmd.trim().toLowerCase();
    if (clean === "clear") {
      setHistory([]);
      return [];
    }
    if (clean.startsWith("track")) {
      const t = clean.split(" ")[1]?.toUpperCase();
      if (t === "RHCSA" || t === "RHCE") {
        setTrack(t);
        return [`[switched track => ${t}]`, `Try: man ${t.toLowerCase()}`];
      }
      return ["usage: track RHCSA | RHCE"];
    }
    if (clean.startsWith("man rhcsa")) return manPage("RHCSA");
    if (clean.startsWith("man rhce")) return manPage("RHCE");
    if (clean.startsWith("syllabus"))
      return [
        `Syllabus (${currentTrack})`,
        ...syllabus[currentTrack].map((s, i) => `  ${i + 1}. ${s}`),
      ];
    if (clean.startsWith("labs"))
      return [
        "Available Labs:",
        ...labs.map((l, i) => `  - ${l.title}: ${l.desc}`),
      ];
    if (clean.startsWith("jobs"))
      return ["Career Paths:", ...careers.map((c) => `  - ${c}`)];
    return [
      `bash: ${cmd.split(" ")[0]}: command not found`,
      "Try: man rhcsa | man rhce | syllabus | labs | jobs | track RHCSA|RHCE | clear",
    ];
  };

  const manPage = (t) => {
    const title =
      t === "RHCSA"
        ? "Red Hat Certified System Administrator"
        : "Red Hat Certified Engineer";
    return [
      `${t}(1)                      Linux Certifications                      ${t}(1)`,
      "",
      `${t} — ${title}`,
      "",
      "SYNOPSIS",
      `   mastery of ${
        t === "RHCSA" ? "core administration" : "advanced automation & services"
      } on Red Hat Enterprise Linux`,
      "",
      "DESCRIPTION",
      ...syllabus[t].map((s, i) => `   ${i + 1}. ${s}`),
      "",
      "SEE ALSO",
      "   labs, jobs, syllabus, track RHCSA|RHCE",
    ];
  };

  const heroButtons = useMemo(
    () => [
      { label: "man rhcsa", action: () => runCommand("man rhcsa") },
      { label: "man rhce", action: () => runCommand("man rhce") },
      { label: "syllabus", action: () => runCommand("syllabus") },
      { label: "labs", action: () => runCommand("labs") },
      { label: "jobs", action: () => runCommand("jobs") },
    ],
    [track]
  );

  return (
    <div className={`${COLORS.bg} ${COLORS.text} min-h-screen overflow-hidden`}>
      <header className="relative text-white py-24 px-6 mt-5">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img
            src="/assets/linux-red-hat-wallpaper-preview.jpg"
            alt="Course Hero"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
            Red Hat Linux Certification (RHCSA & RHCE)
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-6">
            Master Linux system administration, automation, security, and
            hands-on labs to advance your career in IT.
          </p>
          <Link
            to="/contact"
            className="inline-block rounded-xl bg-white text-red-600 font-semibold px-6 py-3 shadow-lg hover:scale-105 transition-transform animate-bounce"
          >
            Enroll Now
          </Link>
        </div>
      </header>

      {/* Terminal Hero */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-5 pb-8 ">
        <div
          className={`rounded-2xl ${COLORS.panel} border ${COLORS.border} shadow-2xl overflow-hidden`}
          data-aos="fade-down"
        >
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-800">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-red-500" />
              <span className="h-3 w-3 rounded-full bg-yellow-500" />
              <span className="h-3 w-3 rounded-full bg-emerald-500" />
            </div>
            <div className="mx-auto text-sm tracking-wide text-neutral-300">
              Red Hat Linux — RHCSA & RHCE · TTY1
            </div>
            <TerminalSquare className="h-4 w-4 text-neutral-500" />
          </div>

          {/* Terminal output */}
          <div
            ref={outputRef}
            className="h-[320px] md:h-[360px] overflow-y-auto px-4 py-4 font-mono text-[13.5px] leading-6 scroll-smooth"
          >
            {history.map((line, i) => (
              <div key={i} className="whitespace-pre-wrap">
                <TerminalLine line={line} />
              </div>
            ))}
            <div className="mt-1">
              <span className={`${COLORS.green}`}>{typed}</span>
              <span className="inline-block w-2 h-4 bg-emerald-400 ml-1 animate-pulse align-middle" />
            </div>
          </div>

          {/* Command palette */}
          <div className="flex flex-wrap items-center gap-2 border-t border-neutral-800 px-4 py-3">
            {heroButtons.map((b) => (
              <button
                key={b.label}
                onClick={b.action}
                className="rounded-md bg-neutral-800 hover:bg-neutral-700/90 border border-neutral-700 px-3 py-1.5 text-sm"
              >
                {b.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-neutral-400">track:</span>
              <button
                onClick={() => runCommand("track RHCSA")}
                className={`px-2.5 py-1 text-xs rounded border ${
                  COLORS.border
                } ${
                  track === "RHCSA"
                    ? "bg-red-600/30 text-red-300"
                    : "bg-neutral-800 text-neutral-300"
                }`}
              >
                RHCSA
              </button>
              <button
                onClick={() => runCommand("track RHCE")}
                className={`px-2.5 py-1 text-xs rounded border ${
                  COLORS.border
                } ${
                  track === "RHCE"
                    ? "bg-red-600/30 text-red-300"
                    : "bg-neutral-800 text-neutral-300"
                }`}
              >
                RHCE
              </button>
              <button
                onClick={() => runCommand("clear")}
                className="px-2.5 py-1 text-xs rounded border border-neutral-700 bg-neutral-800 text-neutral-300"
              >
                clear
              </button>
            </div>
          </div>
        </div>

        {/* Quick CTA */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Link
            to="/contact"
            className="rounded-xl bg-red-600/90 hover:bg-red-600 px-5 py-3 text-white font-semibold text-center shadow-lg transition"
          >
            Enroll Now
          </Link>
          {/* <Link
            to="/"
            className="rounded-xl bg-neutral-800 hover:bg-neutral-700 px-5 py-3 text-neutral-100 text-center font-semibold border border-neutral-700 transition"
          >
            Apply for Next Batch
          </Link> */}
        </div>
      </section>

      {/* Skills */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-black">
          <Shield className="h-5 w-5 text-red-400 animate-bounce" /> Core Skills
          You’ll Build
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {[
            {
              icon: (
                <Server className="h-5 w-5 text-emerald-400 animate-pulse" />
              ),
              label: "SysAdmin",
            },
            {
              icon: <Cpu className="h-5 w-5 text-sky-400 animate-bounce" />,
              label: "Automation",
            },
            {
              icon: <Lock className="h-5 w-5 text-red-400 animate-bounce" />,
              label: "Security",
            },
            {
              icon: (
                <Network className="h-5 w-5 text-amber-400 animate-pulse" />
              ),
              label: "Networking",
            },
            {
              icon: (
                <TerminalSquare className="h-5 w-5 text-purple-400 animate-bounce" />
              ),
              label: "Shell",
            },
            {
              icon: <Shield className="h-5 w-5 text-pink-400 animate-pulse" />,
              label: "SELinux",
            },
          ].map((s, i) => (
            <div
              key={i}
              className={`rounded-lg ${COLORS.panelLight} border ${COLORS.border} px-3 py-3 flex items-center gap-2 hover:border-neutral-600 transition`}
              data-aos="zoom-in"
            >
              <span>{s.icon}</span>
              <span className="text-neutral-200 text-sm">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Labs */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <h3 className="text-lg font-semibold mb-4 text-black flex items-center gap-2">
          <TerminalSquare className="h-5 w-5 text-red-400 animate-bounce" />{" "}
          Hands-on Labs
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
          {labs.map((l, i) => (
            <FlipCardWithIcon
              key={i}
              title={l.title}
              desc={l.desc}
              icon={
                [
                  <Server className="h-6 w-6 text-emerald-400 animate-wiggle" />,
                  <Cpu className="h-6 w-6 text-sky-400 animate-wiggle" />,
                  <Shield className="h-6 w-6 text-red-400 animate-wiggle" />,
                  <TerminalSquare className="h-6 w-6 text-amber-400 animate-wiggle" />,
                ][i]
              }
            />
          ))}
        </div>
      </section>

      {/* Career Paths (auto-scrolling) */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h3 className="text-lg font-semibold mb-4 text-black">Career Paths</h3>
        <div className="overflow-hidden relative">
          <div className="flex gap-3 animate-marquee whitespace-nowrap">
            {careers.map((c, i) => (
              <div
                key={i}
                className={`shrink-0 min-w-[240px] rounded-xl ${COLORS.panelLight} border ${COLORS.border} px-4 py-4 mr-4`}
              >
                <p className="font-medium">{c}</p>
                <p className={`${COLORS.sub} text-sm mt-1`}>
                  Linux + Automation skills are highly valued in modern infra
                  teams.
                </p>
              </div>
            ))}
            {careers.map((c, i) => (
              <div
                key={"dup" + i}
                className={`shrink-0 min-w-[240px] rounded-xl ${COLORS.panelLight} border ${COLORS.border} px-4 py-4 mr-4`}
              >
                <p className="font-medium">{c}</p>
                <p className={`${COLORS.sub} text-sm mt-1`}>
                  Linux + Automation skills are highly valued in modern infra
                  teams.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <h3 className="text-lg font-semibold mb-4 text-black">
          Frequently Asked
        </h3>
        <div className="space-y-3">
          {[
            {
              q: "Do I need prior Linux knowledge?",
              a: "Not much. We start with foundations and quickly move into RHCSA skills.",
            },
            {
              q: "Is the training hands-on?",
              a: "Yes. Expect command-line heavy sessions, service setup, storage & security labs, and Ansible for RHCE.",
            },
            {
              q: "Do you include exam prep?",
              a: "We cover official objectives, mock tasks, and troubleshooting drills aligned to RHCSA/RHCE.",
            },
          ].map((f, i) => (
            <details
              key={i}
              className={`group rounded-lg ${COLORS.panelLight} border ${COLORS.border} p-4`}
            >
              <summary className="flex cursor-pointer items-center justify-between font-medium">
                {f.q} <ChevronDown className="h-4 w-4 text-neutral-400" />
              </summary>
              <p className="mt-2 text-sm text-neutral-300">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <div
          className="relative overflow-hidden rounded-3xl border border-neutral-800 bg-gradient-to-r from-neutral-900 to-neutral-800 p-8 text-center"
          data-aos="zoom-in"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(239,68,68,0.15),transparent_40%),radial-gradient(circle_at_80%_50%,rgba(56,189,248,0.12),transparent_35%)]" />
          <h4 className="relative z-10 text-2xl font-extrabold">
            Become Red Hat Certified — RHCSA & RHCE
          </h4>
          <p className="relative z-10 mt-2 text-neutral-300">
            Master enterprise Linux. Build real reliability with automation and
            security.
          </p>
          <div className="relative z-10 mt-6 flex justify-center gap-3">
            <Link
              to="/contact"
              className="rounded-full bg-red-600 px-6 py-3 text-white font-semibold hover:bg-red-500 transition"
            >
              Talk to Counsellor
            </Link>
            {/* <Link
              to="/apply"
              className="rounded-full border border-neutral-700 px-6 py-3 text-neutral-200 hover:bg-neutral-800 transition"
            >
              Apply Now
            </Link> */}
          </div>
        </div>
      </section>
    </div>
  );
}

/* Small components */
function TerminalLine({ line }) {
  const isPrompt = line.startsWith(PROMPT);
  const isError = line.startsWith("bash:");
  const isHint = line.startsWith("Try:");
  return (
    <div className="font-mono">
      {isPrompt ? (
        <>
          <span className="text-emerald-400">{PROMPT}</span>{" "}
          <span className="text-neutral-200">
            {line.replace(PROMPT + " ", "")}
          </span>
        </>
      ) : isError ? (
        <span className="text-red-400">{line}</span>
      ) : isHint ? (
        <span className="text-sky-400">{line}</span>
      ) : (
        <span className="text-neutral-300">{line}</span>
      )}
    </div>
  );
}

function FlipCardWithIcon({ title, desc, icon }) {
  return (
    <div className="group [perspective:1000px]" data-aos="flip-left">
      <div className="relative h-44 w-full rounded-xl border border-neutral-800 bg-neutral-900/80 transition-transform duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center [backface-visibility:hidden] gap-2">
          <div className="animate-bounce">{icon}</div>
          <p className="text-neutral-100 font-medium">{title}</p>
        </div>
        <div className="absolute inset-0 rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-3 text-neutral-300 [transform:rotateY(180deg)] [backface-visibility:hidden]">
          <p className="text-sm">{desc}</p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            hands-on
          </div>
        </div>
      </div>
    </div>
  );
}
